import { useState } from 'react'
import {
    useGetServiceTypes,
    useCreateServiceType,
    useUpdateServiceType,
    useDeleteServiceType,
} from '../../../../api/routes/services'
import { Button } from '../../../../components/ui/button'
import { Input } from '../../../../components/ui/input'
import { Label } from '../../../../components/ui/label'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../../../../components/ui/dialog'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../../../components/ui/table'
import { Badge } from '../../../../components/ui/badge'
import { toast } from 'react-toastify'

const ServiceTypesSection = ({ deviceId }: { deviceId: string }) => {
    const { data: serviceTypes, isLoading } = useGetServiceTypes(deviceId)
    const createType = useCreateServiceType()
    const updateType = useUpdateServiceType()
    const deleteType = useDeleteServiceType()

    const [showModal, setShowModal] = useState(false)
    const [editingType, setEditingType] = useState<any>(null)
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [intervalCalendarMonths, setIntervalCalendarMonths] = useState('')
    const [intervalMetricKey, setIntervalMetricKey] = useState('')
    const [intervalMetricValue, setIntervalMetricValue] = useState('')
    const [estimatedCost, setEstimatedCost] = useState('')

    const resetForm = () => {
        setName('')
        setDescription('')
        setIntervalCalendarMonths('')
        setIntervalMetricKey('')
        setIntervalMetricValue('')
        setEstimatedCost('')
        setEditingType(null)
    }

    const openCreate = () => {
        resetForm()
        setShowModal(true)
    }

    const openEdit = (type: any) => {
        setEditingType(type)
        setName(type.name || '')
        setDescription(type.description || '')
        setIntervalCalendarMonths(type.intervalCalendarMonths?.toString() || '')
        setIntervalMetricKey(type.intervalMetricKey || '')
        setIntervalMetricValue(type.intervalMetricValue?.toString() || '')
        setEstimatedCost(type.estimatedCost?.toString() || '')
        setShowModal(true)
    }

    const handleSubmit = () => {
        if (!name.trim()) {
            toast.error('Name is required')
            return
        }

        const data: any = {
            name: name.trim(),
            description: description.trim() || null,
            intervalCalendarMonths: intervalCalendarMonths ? parseInt(intervalCalendarMonths) : null,
            intervalMetricKey: intervalMetricKey.trim() || null,
            intervalMetricValue: intervalMetricValue ? parseFloat(intervalMetricValue) : null,
            estimatedCost: estimatedCost ? parseFloat(estimatedCost) : null,
        }

        if (editingType) {
            updateType.mutate(
                { deviceId, typeId: editingType.id, data },
                {
                    onSuccess: () => {
                        toast.success('Service type updated')
                        setShowModal(false)
                        resetForm()
                    },
                    onError: (error: any) => {
                        toast.error(error?.response?.data?.message || 'Failed to update service type')
                    },
                }
            )
        } else {
            createType.mutate(
                { deviceId, data },
                {
                    onSuccess: () => {
                        toast.success('Service type created')
                        setShowModal(false)
                        resetForm()
                    },
                    onError: (error: any) => {
                        toast.error(error?.response?.data?.message || 'Failed to create service type')
                    },
                }
            )
        }
    }

    const handleDelete = (type: any) => {
        if (!window.confirm(`Delete service type "${type.name}"?`)) return

        deleteType.mutate(
            { deviceId, typeId: type.id },
            {
                onSuccess: () => toast.success('Service type deleted'),
                onError: (error: any) => {
                    toast.error(error?.response?.data?.message || 'Failed to delete service type')
                },
            }
        )
    }

    const isSaving = createType.isPending || updateType.isPending

    if (isLoading) {
        return (
            <div className="rounded-lg border bg-card mb-6">
                <div className="px-6 py-4 border-b">
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                        Service Types
                    </h3>
                </div>
                <div className="p-6">
                    <div className="animate-pulse h-24 bg-muted rounded" />
                </div>
            </div>
        )
    }

    const types = serviceTypes || []

    const formatInterval = (type: any) => {
        const parts: string[] = []
        if (type.intervalCalendarMonths) {
            parts.push(`${type.intervalCalendarMonths} months`)
        }
        if (type.intervalMetricKey && type.intervalMetricValue) {
            parts.push(`${Number(type.intervalMetricValue).toLocaleString()} ${type.intervalMetricKey}`)
        }
        return parts.join(' / ') || '-'
    }

    return (
        <>
            <div className="rounded-lg border bg-card mb-6">
                <div className="px-6 py-4 border-b flex items-center justify-between">
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                        Service Types
                    </h3>
                    <Button variant="outline" size="sm" onClick={openCreate}>
                        Add Type
                    </Button>
                </div>
                {types.length === 0 ? (
                    <div className="p-12 text-center">
                        <p className="text-sm text-muted-foreground">
                            No service types defined. Add one to get started.
                        </p>
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Description</TableHead>
                                <TableHead>Interval</TableHead>
                                <TableHead>Est. Cost</TableHead>
                                <TableHead>Source</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {types.map((type: any) => {
                                const isModelDefault = type.businessId === null || type.businessId === undefined
                                return (
                                    <TableRow key={type.id}>
                                        <TableCell className="font-medium">{type.name}</TableCell>
                                        <TableCell className="text-muted-foreground">
                                            {type.description || '-'}
                                        </TableCell>
                                        <TableCell className="text-muted-foreground">
                                            {formatInterval(type)}
                                        </TableCell>
                                        <TableCell className="text-muted-foreground">
                                            {type.estimatedCost != null
                                                ? `$${Number(type.estimatedCost).toFixed(2)}`
                                                : '-'}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant={isModelDefault ? 'secondary' : 'outline'}>
                                                {isModelDefault ? 'Model Default' : 'Business Custom'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => openEdit(type)}
                                                >
                                                    Edit
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="text-destructive hover:text-destructive"
                                                    onClick={() => handleDelete(type)}
                                                    disabled={deleteType.isPending}
                                                >
                                                    Delete
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                )}
            </div>

            <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>
                            {editingType ? 'Edit Service Type' : 'Add Service Type'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="typeName">Name</Label>
                            <Input
                                id="typeName"
                                placeholder="e.g. Oil Change"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="typeDescription">Description</Label>
                            <Input
                                id="typeDescription"
                                placeholder="Optional description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="intervalMonths">Calendar Interval (months)</Label>
                            <Input
                                id="intervalMonths"
                                type="number"
                                placeholder="e.g. 6"
                                value={intervalCalendarMonths}
                                onChange={(e) => setIntervalCalendarMonths(e.target.value)}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="metricKey">Metric Key</Label>
                                <Input
                                    id="metricKey"
                                    placeholder="e.g. engine_hours"
                                    value={intervalMetricKey}
                                    onChange={(e) => setIntervalMetricKey(e.target.value)}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="metricValue">Metric Interval</Label>
                                <Input
                                    id="metricValue"
                                    type="number"
                                    placeholder="e.g. 500"
                                    value={intervalMetricValue}
                                    onChange={(e) => setIntervalMetricValue(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="estCost">Estimated Cost</Label>
                            <Input
                                id="estCost"
                                type="number"
                                step="0.01"
                                placeholder="0.00"
                                value={estimatedCost}
                                onChange={(e) => setEstimatedCost(e.target.value)}
                            />
                        </div>
                    </div>
                    <DialogFooter className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setShowModal(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleSubmit} disabled={isSaving}>
                            {isSaving ? 'Saving...' : editingType ? 'Update' : 'Create'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}

export default ServiceTypesSection
